import type { RecordingCapture } from "@/services/recording-capture"
import {
  dismissSession,
  getIncompleteSessions,
  getSession,
  getUndismissedSession,
  RecordingCaptureMetadata,
  RecordingSession,
} from "@/services/recording-db"
import type { CaptureWarning } from "@/services/recording-health"
import { recordingLocked } from "@/services/recording-lock"
import type { RecordingFinalizationResult } from "@/services/recording-resilient-uploader"
import { RecordingPersistenceError } from "@/services/recording-storage"
import { toast } from "sonner"
import { create } from "zustand"

let finishing: Promise<void> | null = null

interface Uploader {
  ingestChunk: (chunk: Blob) => Promise<void>
  finalize: (capture: RecordingCaptureMetadata) => Promise<RecordingFinalizationResult>
  release: () => Promise<void>
  abort: () => Promise<void>
  cleanup: () => Promise<void>
  getSessionId: () => string | null
  getSessionGuid: () => string | null
  getIngestFailure: () => RecordingPersistenceError | null
  isFull: () => boolean
}

export type ChunkUploadStatus = "idle" | "recording" | "uploading" | "finalizing" | "completed" | "failed"

export type RecordingFailure = "storage" | "persistence"

export type ProcessingStatus =
  | "upload_pending"
  | "uploading"
  | "concatenating"
  | "transcribing"
  | "generating_notes"
  | "completed"
  | "failed"
  | null

interface MeetingRecordingState {
  capture: RecordingCapture | null
  setCapture: (capture: RecordingCapture) => void
  captureWarning: CaptureWarning
  setCaptureWarning: (warning: CaptureWarning) => void
  finishRecording: (reason?: string) => Promise<void>
  isVisible: boolean
  isRecording: boolean
  isPaused: boolean
  mediaRecorder: MediaRecorder | null
  audioChunks: Blob[]
  meetingId: string | null
  meetingTitle: string | null
  startTime: Date | null
  pausedDuration: number
  duration: number
  stream: MediaStream | null
  uploader: Uploader | null
  setUploader: (uploader: Uploader | null) => void
  ingestRecordingChunk: (chunk: Blob) => Promise<void>

  chunkSequence: number
  pendingChunks: number
  uploadedChunks: number
  lastConfirmedSequence: number
  isRecordingFull: boolean
  recordingFailure: RecordingFailure | null
  uploadStatus: ChunkUploadStatus
  setChunkProgress: (progress: {
    totalChunks: number
    uploadedChunks: number
    pendingChunks: number
    lastConfirmedSequence?: number
    full?: boolean
  }) => void
  setUploadStatus: (status: ChunkUploadStatus) => void

  recoverableSessions: RecordingSession[]
  checkForRecovery: () => Promise<void>
  clearRecoverableSessions: () => void
  restorePendingUpload: () => Promise<void>

  processingRequiresConfirmation: boolean
  processingMeetingId: string | null
  processingMeetingTitle: string | null
  processingStatus: ProcessingStatus
  processingSessionId: string | null
  processingSessionGuid: string | null
  processingMimeType: string | null
  isRecordingTransferActive: boolean
  setRecordingTransferActive: (isRecordingTransferActive: boolean) => void
  startProcessing: (meetingId: string, meetingTitle: string | null) => void
  startUploadPending: (opts: {
    meetingId: string
    meetingTitle: string | null
    sessionId: string
    sessionGuid: string
    mimeType: string
    requiresConfirmation?: boolean
  }) => void
  updateProcessingStatus: (status: ProcessingStatus) => void
  clearProcessing: () => void

  startRecording: (
    meetingId: string,
    meetingTitle: string | null,
    mediaRecorder: MediaRecorder,
    stream: MediaStream
  ) => void
  pauseRecording: () => void
  resumeRecording: () => void
  setIsVisible: (isVisible: boolean) => void
  stopRecording: () => Promise<void>
  addAudioChunk: (chunk: Blob) => void
  updateDuration: (duration: number) => void
  reset: () => void
}

export function isRecordingActive(
  state: Pick<MeetingRecordingState, "isRecording" | "isRecordingTransferActive">
): boolean {
  return state.isRecording || state.isRecordingTransferActive
}

export const RECORDING_HEARTBEAT_KEY = "slant:recording-active-at"
// Generous so heavily throttled background timers in the recording tab
// still keep the heartbeat alive for sibling tabs.
export const RECORDING_HEARTBEAT_TTL_MS = 120_000

export function recordingActiveInAnyTab(): boolean {
  if (typeof window === "undefined") return false
  const stored = Number(localStorage.getItem(RECORDING_HEARTBEAT_KEY))
  return Number.isFinite(stored) && Date.now() - stored < RECORDING_HEARTBEAT_TTL_MS
}

export const useMeetingRecordingStore = create<MeetingRecordingState>((set, get) => ({
  capture: null,
  captureWarning: null,
  setCapture: (capture) => set({ capture }),
  setCaptureWarning: (captureWarning) => set({ captureWarning }),
  finishRecording: (reason) => {
    if (finishing) return finishing
    const { capture, uploader, meetingId, meetingTitle, mediaRecorder } = get()
    if (!capture || !uploader || !meetingId) return Promise.resolve()
    const sessionId = uploader.getSessionId()
    const sessionGuid = uploader.getSessionGuid()
    if (!sessionId || !sessionGuid) return Promise.resolve()
    const mimeType = mediaRecorder?.mimeType || "audio/webm"
    get().startProcessing(meetingId, meetingTitle)
    set({ isRecordingTransferActive: true })
    finishing = (async () => {
      try {
        const metadata = await capture.stop(reason)
        await get().stopRecording()
        const result = await uploader.finalize(metadata)
        if (result.outcome === "abandoned") {
          get().clearProcessing()
          toast.error("No audio was saved. Check your microphone and start a new recording.")
        }
      } catch {
        await uploader.release()
        const session = await getSession(sessionGuid).catch(() => undefined)
        get().startUploadPending({
          meetingId,
          meetingTitle,
          sessionId,
          sessionGuid,
          mimeType,
          requiresConfirmation: !session?.capture?.ended_at,
        })
      } finally {
        await uploader.release()
        set({ isRecordingTransferActive: false })
        finishing = null
      }
    })()
    return finishing
  },
  isVisible: false,
  isRecording: false,
  isPaused: false,
  mediaRecorder: null,
  audioChunks: [],
  meetingId: null,
  meetingTitle: null,
  startTime: null,
  pausedDuration: 0,
  duration: 0,
  stream: null,
  uploader: null,
  setUploader: (uploader) => set({ uploader }),

  chunkSequence: 0,
  pendingChunks: 0,
  uploadedChunks: 0,
  lastConfirmedSequence: -1,
  isRecordingFull: false,
  recordingFailure: null,
  uploadStatus: "idle",
  setChunkProgress: (progress) =>
    set((state) => ({
      chunkSequence: progress.totalChunks,
      uploadedChunks: progress.uploadedChunks,
      pendingChunks: progress.pendingChunks,
      lastConfirmedSequence:
        progress.lastConfirmedSequence !== undefined
          ? Math.max(state.lastConfirmedSequence, progress.lastConfirmedSequence)
          : state.lastConfirmedSequence,
      isRecordingFull: progress.full || state.isRecordingFull,
    })),
  setUploadStatus: (status) => set({ uploadStatus: status }),

  recoverableSessions: [],
  checkForRecovery: async () => {
    const sessions = await getIncompleteSessions()
    set({ recoverableSessions: sessions })
  },
  clearRecoverableSessions: () => set({ recoverableSessions: [] }),
  restorePendingUpload: async () => {
    const { isRecording, uploader } = get()
    if (isRecording || uploader) return

    const session = await getUndismissedSession()
    if (!session) return

    if (await recordingLocked(session.sessionGuid)) {
      setTimeout(() => get().restorePendingUpload(), 30_000)
      return
    }

    if (session.sessionId && session.sessionGuid) {
      set({
        processingRequiresConfirmation: !session.capture?.ended_at,
        processingMeetingId: session.meetingId,
        processingMeetingTitle: session.meetingTitle,
        processingStatus: "upload_pending",
        processingSessionId: session.sessionId,
        processingSessionGuid: session.sessionGuid,
        processingMimeType: session.mimeType,
      })
    }
  },

  processingRequiresConfirmation: false,
  processingMeetingId: null,
  processingMeetingTitle: null,
  processingStatus: null,
  processingSessionId: null,
  processingSessionGuid: null,
  processingMimeType: null,
  isRecordingTransferActive: false,
  setRecordingTransferActive: (isRecordingTransferActive) => set({ isRecordingTransferActive }),
  startProcessing: (meetingId, meetingTitle) =>
    set({
      processingRequiresConfirmation: false,
      processingMeetingId: meetingId,
      processingMeetingTitle: meetingTitle,
      processingStatus: "uploading",
      processingSessionId: null,
      processingSessionGuid: null,
      processingMimeType: null,
    }),
  startUploadPending: (opts) =>
    set({
      processingRequiresConfirmation: opts.requiresConfirmation ?? false,
      processingMeetingId: opts.meetingId,
      processingMeetingTitle: opts.meetingTitle,
      processingStatus: "upload_pending",
      processingSessionId: opts.sessionId,
      processingSessionGuid: opts.sessionGuid,
      processingMimeType: opts.mimeType,
    }),
  updateProcessingStatus: (status) => set({ processingStatus: status }),
  clearProcessing: () => {
    const { processingSessionGuid } = get()
    if (processingSessionGuid) {
      dismissSession(processingSessionGuid)
    }
    set({
      processingMeetingId: null,
      processingMeetingTitle: null,
      processingStatus: null,
      processingSessionId: null,
      processingSessionGuid: null,
      processingMimeType: null,
      isRecordingTransferActive: false,
    })
  },

  ingestRecordingChunk: async (chunk: Blob) => {
    const { uploader } = get()
    if (uploader) {
      try {
        await uploader.ingestChunk(chunk)
      } catch (error) {
        const persistenceError = error instanceof RecordingPersistenceError ? error : null
        set({
          recordingFailure: persistenceError?.diagnostics.quotaExceeded ? "storage" : "persistence",
        })
        void get().finishRecording("persistence_failure")
      }
    }
  },

  startRecording: (meetingId, meetingTitle, mediaRecorder, stream) => {
    set({
      isVisible: true,
      isRecording: true,
      isPaused: false,
      mediaRecorder,
      stream,
      meetingId,
      meetingTitle,
      startTime: new Date(),
      audioChunks: [],
      duration: 0,
      pausedDuration: 0,
      chunkSequence: 0,
      pendingChunks: 0,
      uploadedChunks: 0,
      lastConfirmedSequence: -1,
      isRecordingFull: false,
      recordingFailure: null,
      uploadStatus: "recording",
    })
  },

  pauseRecording: () => {
    const { mediaRecorder, duration } = get()
    if (mediaRecorder && mediaRecorder.state === "recording") {
      get().capture?.pause()
      // Save the current duration when pausing
      set({ isPaused: true, pausedDuration: duration })
    }
  },

  resumeRecording: () => {
    const { mediaRecorder } = get()
    if (mediaRecorder && mediaRecorder.state === "paused") {
      get().capture?.resume()
      // Resume with a new start time but keep accumulated duration
      set({ isPaused: false, startTime: new Date() })
    }
  },

  setIsVisible: (isVisible: boolean) => {
    set({ isVisible })
  },

  stopRecording: async () => {
    await get().capture?.stop()
    set({
      isRecording: false,
      isPaused: false,
      mediaRecorder: null,
      stream: null,
      startTime: null,
      pausedDuration: 0,
      duration: 0,
      capture: null,
      captureWarning: null,
      uploader: null,
      uploadStatus: "idle",
    })
  },

  addAudioChunk: (chunk) => {
    // Retained for backward compatibility; not used in the chunked flow.
    set((state) => ({ audioChunks: [...state.audioChunks, chunk] }))
  },

  updateDuration: (duration) => {
    set({ duration })
  },

  reset: () => {
    const { stream, mediaRecorder } = get()

    if (mediaRecorder || stream) {
      void get().finishRecording("recording_reset")
      return
    }

    set({
      isRecording: false,
      isVisible: false,
      isPaused: false,
      mediaRecorder: null,
      audioChunks: [],
      meetingId: null,
      meetingTitle: null,
      startTime: null,
      pausedDuration: 0,
      duration: 0,
      stream: null,
      uploader: null,
      chunkSequence: 0,
      pendingChunks: 0,
      uploadedChunks: 0,
      lastConfirmedSequence: -1,
      isRecordingFull: false,
      recordingFailure: null,
      uploadStatus: "idle",
      isRecordingTransferActive: false,
    })
  },
}))
